import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  getWebsiteImagesAdmin,
  updateWebsiteImage,
  uploadTeamImage,
  adminLogout,
  verifyAdminToken
} from '../services/api';
import { Loader2, Edit, LogOut, User, Upload, X, ArrowLeft } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminImagesPage = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [newImageUrl, setNewImageUrl] = useState('');

  const checkAuth = useCallback(async () => {
    try {
      await verifyAdminToken();
    } catch (error) {
      toast({
        title: "Authentication Failed",
        description: "Please login again",
        variant: "destructive"
      });
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
    fetchImages();
  }, [checkAuth]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const data = await getWebsiteImagesAdmin();
      setImages(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load images",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully"
    });
  };

  const handleOpenDialog = (image) => {
    setEditingImage(image);
    setNewImageUrl(image.imageUrl);
    const imageUrl = (image.imageUrl.startsWith('/api/uploads') || image.imageUrl.startsWith('/uploads'))
      ? `${BACKEND_URL}${image.imageUrl}`
      : image.imageUrl;
    setPreviewUrl(imageUrl);
    setSelectedFile(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingImage(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    setNewImageUrl('');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Invalid File",
          description: "Please select a JPG, PNG, or WebP image",
          variant: "destructive"
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Please select an image smaller than 5MB",
          variant: "destructive"
        });
        return;
      }

      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(editingImage?.imageUrl || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let finalImageUrl = newImageUrl;

      if (selectedFile) {
        setUploading(true);
        try {
          const uploadResult = await uploadTeamImage(selectedFile);
          finalImageUrl = uploadResult.image_url;
        } catch (uploadError) {
          toast({
            title: "Upload Failed",
            description: "Failed to upload image",
            variant: "destructive"
          });
          setLoading(false);
          setUploading(false);
          return;
        }
        setUploading(false);
      }

      await updateWebsiteImage(editingImage.id, { imageUrl: finalImageUrl });
      
      toast({
        title: "Success!",
        description: "Image updated successfully"
      });
      
      handleCloseDialog();
      await fetchImages();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update image",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const username = localStorage.getItem('admin_username') || 'Admin';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                size="sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Website Images</h1>
                <p className="text-sm text-gray-600">Manage images throughout your website</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-gray-700">
                <User className="h-5 w-5" />
                <span className="font-medium">{username}</span>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((image) => (
              <Card key={image.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative h-48">
                  <img
                    src={
                      (image.imageUrl.startsWith('/api/uploads') || image.imageUrl.startsWith('/uploads'))
                        ? `${BACKEND_URL}${image.imageUrl}`
                        : image.imageUrl
                    }
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {image.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {image.description}
                  </p>
                  <Button
                    onClick={() => handleOpenDialog(image)}
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Change Image
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Change {editingImage?.title}</DialogTitle>
            <DialogDescription>
              {editingImage?.description}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Photo Upload Section */}
            <div>
              <Label>New Image</Label>
              <div className="mt-2 space-y-4">
                {/* File Upload Button */}
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer">
                    <div className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                      <Upload className="h-4 w-4" />
                      <span>Upload Image</span>
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                  <span className="text-sm text-gray-600">
                    {selectedFile ? selectedFile.name : 'JPG, PNG or WebP (max 5MB)'}
                  </span>
                </div>

                {/* Image Preview */}
                {previewUrl && (
                  <div className="relative inline-block">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full max-w-md h-48 object-cover rounded-lg border-2 border-gray-200"
                    />
                    {selectedFile && (
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}

                {/* Divider */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="text-sm text-gray-500">OR</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* URL Input Option */}
                <div>
                  <Label htmlFor="imageUrl">Or use Image URL</Label>
                  <Input
                    id="imageUrl"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    className="mt-1"
                    placeholder="https://images.unsplash.com/photo-..."
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                disabled={loading || uploading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || uploading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Update Image'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
};

export default AdminImagesPage;
